import { useState, useEffect, useRef } from "react";

// @mui material components
import {
  Grid,
  TextField,
  Box,
  Chip,
  Stack,
  IconButton,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import type { Dayjs } from "dayjs";
import Icon from "@mui/material/Icon";

import { DRAWER_FORM_GRID_SPACING, drawerReadonlyFieldSx } from "../../../../lib/ui/dialogFormLayout";
import { DrawerForm, DrawerFormSection } from "../../../../components/DrawerForm";
import { FormHiddenField, IconAutocomplete } from "../../../../components/forms";
import { useAnomalyGuideClassification } from "../../lib/useAnomalyGuideClassification";
import { useDisplayLocalization } from "../../../../lib/datetime/DisplayLocalizationProvider";
import { toApiUtcDateTime, toDisplayDayjs } from "../../../../lib/datetime/displayDateTime";
import {
  INCIDENT_PRIORITY_OPTIONS,
  normalizeIncidentPriority,
} from "../../lib/constants";
import { useTranslation } from "react-i18next";

const EditIncidentBody = (item: any) => {
  const { t } = useTranslation();
  const { timeZone } = useDisplayLocalization();
  const [selectedPriority, setSelectedPriority] = useState<string | null>(
    normalizeIncidentPriority(item?.priority) ?? item?.priority ?? null,
  );
  const [detectedAt, setDetectedAt] = useState<Dayjs | null>(
    item?.detectedAt ? toDisplayDayjs(item.detectedAt) : toDisplayDayjs(new Date()),
  );
  const [tags, setTags] = useState<string[]>(item?.tags || []);
  const [tagInput, setTagInput] = useState('');
  const [description, setDescription] = useState(item?.description || "");
  const appliedClassification = useRef(
    `${item?.category?.trim() || ""}|${item?.subcategory?.trim() || ""}`,
  );
  const {
    category,
    subcategory,
    setCategory,
    setSubcategory,
    categoryOptions,
    subcategoryOptions,
    classificationGuidance,
    taxonomyReady,
    taxonomyLoading,
  } = useAnomalyGuideClassification(
    { category: item?.category, subcategory: item?.subcategory },
    `${item?.id ?? ""}|${item?.category ?? ""}|${item?.subcategory ?? ""}`,
  );

  const priorityOptions = INCIDENT_PRIORITY_OPTIONS;

  // Tag functions
  const addTag = () => {
    const trimmedTag = tagInput.trim();
    if (trimmedTag && !tags.includes(trimmedTag)) {
      setTags([...tags, trimmedTag]);
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const handleTagKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      addTag();
    }
  };

  // Update state when item prop changes
  useEffect(() => {
    if (item?.priority) {
      setSelectedPriority(item.priority);
    }
    if (item?.detectedAt) {
      setDetectedAt(toDisplayDayjs(item.detectedAt));
    }
    if (item?.tags) {
      setTags(item.tags);
    }
  }, [item]);

  useEffect(() => {
    const pair = `${category?.trim() || ""}|${subcategory?.trim() || ""}`;
    if (pair === appliedClassification.current) return;
    if (!taxonomyReady || !category || !subcategory || classificationGuidance == null) return;
    appliedClassification.current = pair;
    const original = `${item?.category?.trim() || ""}|${item?.subcategory?.trim() || ""}`;
    setDescription(pair === original ? item?.description || "" : classificationGuidance);
  }, [taxonomyReady, category, subcategory, classificationGuidance, item?.category, item?.subcategory, item?.description]);

  return (
    <DrawerForm>
      <DrawerFormSection title={t("form.summary")}>
        <Grid container spacing={DRAWER_FORM_GRID_SPACING}>
          <Grid size={12}>
            <TextField
              fullWidth
              id="id"
              name="id"
              label={t("common.id")}
              variant="outlined"
              size="small"
              value={item?.id || ""}
              InputProps={{ readOnly: true }}
              sx={drawerReadonlyFieldSx}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              required
              fullWidth
              id="title"
              name="title"
              label={t("common.title")}
              variant="outlined"
              size="small"
              defaultValue={item?.title || ""}
              placeholder={t("form.incidentTitle")}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              required
              fullWidth
              id="description"
              name="description"
              label={t("common.description")}
              variant="outlined"
              size="small"
              multiline
              rows={2}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder={t("form.describeIncident")}
            />
          </Grid>
          <Grid size={6}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateTimePicker
                label={t("incidents.detectedAt")}
                timezone={timeZone}
                value={detectedAt}
                onChange={(newValue) => setDetectedAt(newValue)}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                  },
                }}
              />
            </LocalizationProvider>
            <FormHiddenField
              name="detectedAt"
              value={detectedAt ? toApiUtcDateTime(detectedAt) || "" : ""}
            />
          </Grid>
          <Grid size={6}>
            <IconAutocomplete
              options={priorityOptions}
              value={selectedPriority}
              onChange={setSelectedPriority}
              label={t("incidents.priority")}
              noOptionsText="No priority options found"
            />
            <FormHiddenField name="priority" value={selectedPriority || ""} />
          </Grid>
        </Grid>
      </DrawerFormSection>

      <DrawerFormSection title={t("form.classification")}>
        <Grid container spacing={DRAWER_FORM_GRID_SPACING}>
          <Grid size={6}>
            <IconAutocomplete
              options={categoryOptions}
              value={category}
              onChange={setCategory}
              label={t("incidents.category")}
              placeholder={t("anomalies.selectCategory")}
              required
              loading={taxonomyLoading}
              noOptionsText={
                taxonomyLoading
                  ? t("anomalies.form.loadingSubcategories")
                  : t("anomalies.form.noSubcategories")
              }
            />
            <FormHiddenField name="category" value={category || ""} />
          </Grid>
          <Grid size={6}>
            <IconAutocomplete
              options={subcategoryOptions}
              value={subcategory}
              onChange={setSubcategory}
              label={t("incidents.subcategory")}
              placeholder={
                category ? t("anomalies.form.searchSubcategory") : t("anomalies.selectCategoryFirst")
              }
              required
              disabled={!category}
              loading={taxonomyLoading}
              noOptionsText={
                !category
                  ? t("anomalies.selectCategoryFirstLong")
                  : taxonomyLoading
                    ? t("anomalies.form.loadingSubcategories")
                    : t("anomalies.form.noSubcategories")
              }
            />
            <FormHiddenField name="subcategory" value={subcategory || ""} />
          </Grid>
          <Grid size={12}>
            <TextField
              fullWidth
              id="sourceSystemId"
              name="sourceSystemId"
              label={t("incidents.sourceSystemId")}
              variant="outlined"
              size="small"
              defaultValue={item?.sourceSystemId || ""}
            />
          </Grid>
        </Grid>
      </DrawerFormSection>

      <DrawerFormSection title={t("form.assignment")}>
        <Grid container spacing={DRAWER_FORM_GRID_SPACING}>
          <Grid size={6}>
            <TextField
              fullWidth
              id="assignedTo"
              name="assignedTo"
              label={t("incidents.assignedTo")}
              variant="outlined"
              size="small"
              defaultValue={item?.assignedTo || ""}
              placeholder={t("common.username")}
            />
          </Grid>
          <Grid size={6}>
            <TextField
              fullWidth
              id="assignedGroup"
              name="assignedGroup"
              label={t("incidents.group")}
              variant="outlined"
              size="small"
              defaultValue={item?.assignedGroup || ""}
              placeholder={t("form.groupName")}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              fullWidth
              id="openSearchId"
              name="openSearchId"
              label={t("incidents.databaseId")}
              variant="outlined"
              size="small"
              defaultValue={item?.openSearchId || ""}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              fullWidth
              id="tagInput"
              label={t("incidents.tags")}
              variant="outlined"
              size="small"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyPress={handleTagKeyPress}
              InputProps={{
                endAdornment: (
                  <IconButton size="small" onClick={addTag} disabled={!tagInput.trim()}>
                    <Icon fontSize="small">add</Icon>
                  </IconButton>
                ),
              }}
            />
            {tags.length > 0 && (
              <Box sx={{ mt: 0.75 }}>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {tags.map((tag, index) => (
                    <Chip
                      key={index}
                      label={tag}
                      variant="outlined"
                      color="primary"
                      size="small"
                      onDelete={() => removeTag(tag)}
                      deleteIcon={<Icon fontSize="small">close</Icon>}
                    />
                  ))}
                </Stack>
              </Box>
            )}
            <FormHiddenField name="tags" value={tags.join(",")} />
          </Grid>
        </Grid>
      </DrawerFormSection>
    </DrawerForm>
  );
};

export default EditIncidentBody;